/**
 * Metric keys and standard event names. Mirrors docs/METRICS.md. `daily_metrics.metric`
 * stores one of METRIC_KEYS; the SDK emits STANDARD_EVENTS.
 */

export const METRIC_KEYS = [
  "mrr",
  // legacy: "mrr" was the excl.-VAT view; no longer written or read (old rows remain). The two live modes are below.
  "mrr_gross",
  "mrr_proceeds",
  "arr",
  "revenue",
  "proceeds",
  "active_subscriptions",
  "active_trials",
  "new_paid",
  "lifetime_purchases",
  "trial_conversion",
  "churn_rate",
  "refund_rate",
  "arppu",
  "ltv_realized",
  "ltv_estimated",
  // Active-on-phone
  "active_on_phone",
  "active_on_phone_opened",
  "active_on_phone_passive",
  "asc_installs",
  "asc_deletions",
  "asc_net_installed",
  // App Store rating snapshot (nightly iTunes lookup)
  "app_rating",
  "app_rating_count",
  // Usage
  "dau",
  "mau",
  "stickiness",
  "new_users",
  "retention_d1",
  "retention_d7",
  "retention_d30",
  "paywall_conversion",
] as const;

export type MetricKey = (typeof METRIC_KEYS)[number];

/** Virtual app id used in daily_metrics for the all-apps total. */
export const ALL_APPS_ID = "_all";

/** Standard SDK events (docs/METRICS.md). Free-form events are allowed alongside these. */
export const STANDARD_EVENTS = [
  "app_open",
  "session_start",
  "paywall_viewed",
  "purchase_started",
  "purchase_completed",
  "purchase_cancelled",
] as const;

/** Retention columns (days after the first event day). */
export const RETENTION_DAYS = [1, 7, 14, 30] as const;

export type StandardEvent = (typeof STANDARD_EVENTS)[number];

/** Active-on-phone window: a device counts as active with any sign of life in 7 days. */
export const ACTIVE_ON_PHONE_WINDOW_DAYS = 7;

/** Widget heartbeats are throttled client-side to at most once per this interval. */
export const WIDGET_HEARTBEAT_THROTTLE_HOURS = 6;

/** Dashboard amount switch: gross, or net proceeds after platform costs (default). No VAT estimate. */
export const VAT_MODES = ["gross", "proceeds"] as const;
export type VatMode = (typeof VAT_MODES)[number];
export const DEFAULT_VAT_MODE: VatMode = "proceeds";

export const PERIOD_KEYS = ["7d", "30d", "90d", "12m"] as const;
export type PeriodKey = (typeof PERIOD_KEYS)[number];
/** Length in days of each period key (12m = 365). */
export const PERIOD_DAYS: Readonly<Record<PeriodKey, number>> = { "7d": 7, "30d": 30, "90d": 90, "12m": 365 };

/** daily_metrics metric that stores the MRR for a VAT mode (the legacy "mrr" key is no longer used). */
export const MRR_METRIC_BY_VAT: Readonly<Record<VatMode, MetricKey>> = {
  gross: "mrr_gross",
  proceeds: "mrr_proceeds",
};

/** Rounds half away from zero, so a sale and its refund round to exact opposites. */
export function roundHalfAway(n: number): number {
  return (n < 0 ? -Math.round(-n) : Math.round(n)) + 0; // + 0 turns -0 into 0
}

/**
 * Billing periods per month, for MRR normalisation. Apple periods come from derivePeriod()
 * (P1W, P1M, P2M, P3M, P6M, P1Y); a few extra ISO shapes are accepted. Unknown -> null.
 */
export function monthsInPeriod(period: string): number | null {
  const m = /^P(\d+)([WMYD])$/.exec(period.trim().toUpperCase());
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n <= 0) return null;
  switch (m[2]) {
    case "D":
      return (n * 12) / 365;
    case "W":
      return (n * 12) / 52;
    case "M":
      return n;
    default:
      return n * 12;
  }
}

/**
 * Normalises a per-period price to a monthly amount (same currency, minor units, NOT rounded:
 * callers round once after the FX conversion so the sum never drifts). Year / 12, week x 52 / 12.
 * An unknown period is treated as monthly (the Apple default in derivePeriod).
 */
export function normalizeToMonthlyMinor(priceMinor: number, period: string): number {
  const months = monthsInPeriod(period) ?? 1;
  return priceMinor / months;
}

/**
 * Compares dotted numeric versions ("1.10.0" > "1.9.2"). A side without a numeric prefix (empty / "beta") is the LOWEST, so
 * a real numeric version always wins; two non-numeric sides compare as strings.
 */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string): number[] | null => {
    const m = /^\d+(?:\.\d+)*/.exec(v.trim());
    return m ? m[0].split(".").map(Number) : null;
  };
  const pa = parse(a);
  const pb = parse(b);
  if (!pa && !pb) return a < b ? -1 : a > b ? 1 : 0;
  if (!pa) return -1;
  if (!pb) return 1;
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

/**
 * Adoption of the newest version in a device split (`DeviceCount[]`, keys are versions). "unknown" (no version reported)
 * is left out of both the pick and the denominator, "other" (the split's tail bucket) counts as a non-newest bucket.
 * Null when no device reports a version.
 */
export function latestVersionShare(split: ReadonlyArray<{ key: string; count: number }>): { latest: string; share: number; devices: number } | null {
  const known = split.filter((r) => r.key !== "unknown" && r.count > 0);
  const devices = known.reduce((s, r) => s + r.count, 0);
  if (devices === 0) return null;
  const versions = known.filter((r) => r.key !== "other");
  if (versions.length === 0) return null;
  const newest = versions.reduce((best, r) => (compareVersions(r.key, best.key) > 0 ? r : best));
  return { latest: newest.key, share: newest.count / devices, devices };
}

/** A whale share is only reported with at least this many paying customers. */
export const WHALE_MIN_CUSTOMERS = 10;
